import {
  ConfirmModalArgs,
  useConfirmModal,
} from '@/components/ConfirmationModal';
import { useSubmitInvoiceReport } from '@/context/services/reportsType/invoiceReports/Edit.service';
import { TPMData } from '@/context/services/tpm/type';
import { InvoiceKindValues } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/services/type';
import { useInvoiceReportNavigation } from '@/features/InvoiceReport/Edit/components/hooks/useGetInvoiceReportPath';
import {
  ApiErrors,
  convertFlashMessageTypeToInlineNotificationType,
  ErrorFields,
} from '@/features/InvoiceReport/Edit/components/type';
import { isInvoiceKindDetectionTarget } from '@/features/InvoiceReport/Edit/components/utils/invoiceKindDetection';
import {
  InvoiceTransaction,
  PaymentRequestForm,
  TransactionProperty,
} from '@/features/InvoiceReport/Edit/type';
import { useTranslation } from '@/i18n';
import yup from '@/libs/validation';
import { useParams } from '@/router';
import { parseErrorSchema, SendErrorTracking } from '@/utils/errors';
import { InlineNotificationProps } from '@moneyforward/ap-frontend-components';
import {
  FormInputIdMap,
  InvoiceReportDetailFormInputsResponse,
  InvoiceReportDetailResponseFlashMessagesItem,
  useCsrfToken,
} from 'ap-openapi';
import dayjs from 'dayjs';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import {
  Control,
  ErrorOption,
  FieldPath,
  SubmitHandler,
  useWatch,
} from 'react-hook-form';
import { ValidationError } from 'yup';
import { useFormValue, useInvoiceReportsRoute } from '../Context/API';
import { useGetExItem } from '../InvoiceTransaction/hooks/useGetExItem';
import { useGetInvoiceKind } from '../InvoiceTransaction/hooks/useInvoiceKind';
import { useSelectorExcise } from '../InvoiceTransaction/hooks/useSelectorExcise';

export const useReportForm = () => {
  const { getIsZeroPer } = useSelectorExcise();
  const getInvoiceKind = useGetInvoiceKind();
  const { getDrExciseIdFromExItem } = useGetExItem();
  const route = useInvoiceReportsRoute();
  const { status } = useFormValue();

  const resolveExciseId = useCallback(
    (invoice: InvoiceTransaction): string | undefined => {
      if (invoice.drExciseId) {
        return invoice.drExciseId;
      } else {
        return getDrExciseIdFromExItem(invoice.exItemId);
      }
    },
    [getDrExciseIdFromExItem]
  );

  const updateInvoiceKind = useCallback(
    (
      invoice: InvoiceTransaction,
      hasSpecialException: boolean,
      isDutyFree: boolean,
      tpmData: TPMData
    ): InvoiceTransaction => {
      const dealDate = dayjs(invoice.dealDate);
      const exciseId = resolveExciseId(invoice);
      const invoiceKind = getInvoiceKind({
        dealDate: dealDate,
        tpmData,
        isZeroPer: getIsZeroPer(exciseId),
        hasSpecialException: Boolean(hasSpecialException),
        isDutyFree,
      });
      invoice.invoiceKind = invoiceKind;
      invoice.isInvoiceKindDetectionTarget = isInvoiceKindDetectionTarget(
        route,
        status
      );
      return invoice;
    },
    [getInvoiceKind, getIsZeroPer, resolveExciseId, route, status]
  );

  const getInvoiceKindValue = useCallback(
    (
      hasSpecialException: boolean,
      isDutyFree: boolean,
      tpmData: TPMData,
      exciseId?: string,
      dealDateValue?: string
    ): InvoiceKindValues | '' => {
      const dealDate = dayjs(dealDateValue || '');
      const invoiceKind = getInvoiceKind({
        dealDate: dealDate,
        tpmData,
        isZeroPer: getIsZeroPer(exciseId),
        hasSpecialException: Boolean(hasSpecialException),
        isDutyFree,
      });
      return invoiceKind;
    },
    [getInvoiceKind, getIsZeroPer]
  );

  return { updateInvoiceKind, getInvoiceKindValue };
};

export const useHasSpecialException = (
  specialExceptionValue: PaymentRequestForm['specialException']
) => {
  return useMemo(() => {
    if (!specialExceptionValue) {
      return false;
    }

    return (
      specialExceptionValue.value !== null &&
      specialExceptionValue.value !== undefined &&
      specialExceptionValue.value !== '0'
    );
  }, [specialExceptionValue]);
};

export const useHasSpecialExceptionWithControl = (
  control: Control<PaymentRequestForm>
) => {
  const specialExceptionValue = useWatch<
    PaymentRequestForm,
    'specialException'
  >({
    control: control,
    name: 'specialException',
  });
  return useHasSpecialException(specialExceptionValue);
};

export const useFilteredIsHiddenReportFormInputs = (
  reportFormInputTypeInfo: InvoiceReportDetailFormInputsResponse[]
) => reportFormInputTypeInfo.filter(({ is_hidden }) => !is_hidden);

export const useSubmitPaymentReport = (
  schema: yup.ObjectSchema<{
    invoiceTransactions: Partial<InvoiceTransaction>[];
  }>,
  formInputIdMap: FormInputIdMap,
  invoicePayeeTypeInfo: InvoiceReportDetailFormInputsResponse | undefined,
  invoiceFileTypeId: string,
  setError: (name: FieldPath<PaymentRequestForm>, error: ErrorOption) => void,
  handleInlineNotificationErrors: (error: ErrorFields | ApiErrors) => boolean,
  handleInvalidScroll: (ele: Element | undefined, name: string) => void,
  validateMinusTotalValue: () => boolean,
  isHaveSetApPayeeIdByDefault: boolean,
  isValidateMaximumInvoiceTransactionCountFlag: boolean,
  transactionProperties: TransactionProperty[] | undefined,
  apInferenceIds: string[],
  onClearInferenceIds: () => void
) => {
  const [isLoading, setLoading] = useState(false);

  const getDateFormatType = useMemo(
    () =>
      (propertyId: string): 'year_month' | undefined => {
        const property = transactionProperties?.find(
          (prop) => prop.id === propertyId && prop.componentType === 'date'
        );
        return property?.dateFormatType === 'year_month'
          ? 'year_month'
          : undefined;
      },
    [transactionProperties]
  );

  const submitReportDraft = useSubmitInvoiceReport(
    formInputIdMap,
    invoicePayeeTypeInfo,
    invoiceFileTypeId,
    isHaveSetApPayeeIdByDefault,
    true,
    getDateFormatType
  );
  const submitReportNonDraft = useSubmitInvoiceReport(
    formInputIdMap,
    invoicePayeeTypeInfo,
    invoiceFileTypeId,
    isHaveSetApPayeeIdByDefault,
    false,
    getDateFormatType
  );
  const onSaveSubmit = useCallback(
    async (
      values: PaymentRequestForm,
      isDraft: boolean,
      inferenceIds?: string[]
    ) => {
      const submitReport =
        isValidateMaximumInvoiceTransactionCountFlag && isDraft
          ? submitReportDraft
          : submitReportNonDraft;
      handleInlineNotificationErrors({
        invoiceReport: undefined,
        invoiceTransactions: undefined,
        invoiceTransactionsAlert: undefined,
        invoiceTransactionsRow: undefined,
      });
      const isValid = validateMinusTotalValue();
      if (!isValid) {
        return;
      }
      try {
        const { invoiceReportResponse, invoiceTransactionResponse } =
          await submitReport(values, inferenceIds);
        const newErrors: ApiErrors = {
          invoiceReport:
            invoiceReportResponse?.status === 'error' ||
            invoiceReportResponse?.status === 'invalid'
              ? invoiceReportResponse?.errors?.messages?.join('<br/>')
              : undefined,
          invoiceTransactions:
            invoiceTransactionResponse?.status === 'error'
              ? invoiceTransactionResponse?.errors?.messages?.join('<br/>')
              : undefined,
          invoiceTransactionsAlert: undefined,
          invoiceTransactionsRow:
            invoiceTransactionResponse?.status === 'error'
              ? invoiceTransactionResponse?.errors
              : undefined,
        };
        handleInlineNotificationErrors(newErrors);
        return !newErrors.invoiceReport && !newErrors.invoiceTransactions;
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        throw new SendErrorTracking(err as unknown as Error);
      }
    },
    [
      isValidateMaximumInvoiceTransactionCountFlag,
      submitReportDraft,
      submitReportNonDraft,
      handleInlineNotificationErrors,
      validateMinusTotalValue,
    ]
  );

  const { onDeleteSubmit, DeleteForm, ConfirmModal } = useDeleteForm();
  const {
    goToInvoiceReportIndex,
    goToInvoiceReportSelectApprover,
    goToInvoiceReportShow,
  } = useInvoiceReportNavigation();
  const actionRef = useRef('');
  const onSubmit: SubmitHandler<PaymentRequestForm> = useCallback(
    async (values: PaymentRequestForm) => {
      try {
        setLoading(true);
        try {
          await schema.validate(values, {
            abortEarly: false,
          });
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          if (!(e instanceof ValidationError)) {
            throw e;
          }
          const errFields = parseErrorSchema(e, true);
          Object.entries(errFields).forEach(([key, err]) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            setError(key as FieldPath<PaymentRequestForm>, err);
          });
          const errNames = Object.keys(errFields);
          if (errNames.length > 0 && errNames[0]) {
            handleInvalidScroll(undefined, errNames[0]);
          }
          if (handleInlineNotificationErrors(errFields)) {
            return;
          }
        }
        const action = actionRef.current;
        switch (action) {
          case 'draft': {
            const success = await onSaveSubmit(values, true, apInferenceIds);
            if (success) {
              onClearInferenceIds();
              goToInvoiceReportIndex();
            }
            break;
          }
          case 'savedByApproving': {
            const success = await onSaveSubmit(values, false, apInferenceIds);
            if (success) {
              onClearInferenceIds();
              goToInvoiceReportShow();
            }
            break;
          }
          case 'next': {
            const success = await onSaveSubmit(values, false, apInferenceIds);
            if (success) {
              onClearInferenceIds();
              goToInvoiceReportSelectApprover();
            }
            break;
          }
          default:
            break;
        }
      } finally {
        setLoading(false);
      }
    },
    [
      schema,
      handleInlineNotificationErrors,
      setError,
      handleInvalidScroll,
      onSaveSubmit,
      apInferenceIds,
      onClearInferenceIds,
      goToInvoiceReportIndex,
      goToInvoiceReportShow,
      goToInvoiceReportSelectApprover,
    ]
  );
  return {
    onSubmit,
    actionRef,
    isLoading,
    onDeleteSubmit,
    DeleteForm,
    ConfirmModal,
  };
};

export const useConfirm = () => {
  const { t } = useTranslation();
  const args: ConfirmModalArgs = useMemo(
    () => ({
      title: t('payment_request_delete_title'),
      body: t('payment_request_delete_body'), // この申請を削除してよいですか？
      okButtonProps: {
        color: 'danger',
        children: t('delete'),
      },
    }),
    [t]
  );
  return useConfirmModal(args);
};

const useDeleteForm = () => {
  const ref = useRef<HTMLFormElement | null>(null);
  const { confirm, ConfirmModal } = useConfirm();

  const onDeleteSubmit = useCallback(async () => {
    if (ref.current) {
      if (await confirm()) {
        ref.current.submit();
      }
    }
  }, [confirm]);
  const { report_type_id, report_id } = useParams(
    '/report_types/:report_type_id/invoice_reports/:report_id/edit'
  );
  const url = `/report_types/${report_type_id}/invoice_reports/${report_id}`;
  const csrfToken = useCsrfToken();
  const DeleteForm: FC = useCallback(() => {
    return (
      <form ref={ref} action={url} method='post' hidden>
        <input name='_method' value='delete' hidden />
        <input name='authenticity_token' value={csrfToken} hidden />
      </form>
    );
  }, [csrfToken, url]);
  return {
    DeleteForm,
    ConfirmModal,
    onDeleteSubmit,
  };
};

type FlashMessageInlineProps = {
  type: InlineNotificationProps['type'];
  message?: string;
};
export const useFlashMessageInvoiceReport = (
  messages: InvoiceReportDetailResponseFlashMessagesItem[] | undefined
): FlashMessageInlineProps[] | undefined => {
  return useMemo(() => {
    if (!messages) return undefined;
    const grouped: Record<string, FlashMessageInlineProps> = messages.reduce(
      (acc: Record<string, FlashMessageInlineProps>, curr) => {
        const key = convertFlashMessageTypeToInlineNotificationType(curr.type);
        if (acc[key]) {
          if (curr.message) {
            acc[key] = {
              ...curr,
              type: key,
              message: acc[key]?.message
                ? `${acc[key]?.message}<br/>${curr.message}`
                : curr.message,
            };
          }
        } else {
          acc[key] = { ...curr, type: key };
        }
        return acc;
      },
      {}
    );
    return Object.values(grouped);
  }, [messages]);
};
